import { useState } from "react";
import usePersistedState from "../../lib/usePersistedState";
import { trackEvent } from "../../lib/analytics";
import { looksLikeStoreAddress } from "../RequestForm";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

/* The audit asks for a URL and nothing else: the score is free, the email
   comes later at the unlock gate. On success we hand off to the audit page,
   which owns the loading experience. The BFCM readiness door reuses this
   form with its own createPath, CTA label and tracking form name. */
export default function AovAuditForm({ createPath, ctaLabel = "Run my free audit", trackForm = "aov_audit" }) {
  const [domain, setDomain] = usePersistedState("audit.domain");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (event) => {
    event.preventDefault();
    if (!looksLikeStoreAddress(domain)) {
      setErrors({ domain: ["Enter your .myshopify.com domain or your store's web address (like https://yourstore.com)."] });
      return;
    }
    setSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(createPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ domain }),
      });
      const data = await response.json();

      if (response.ok) {
        trackEvent("aov_audit_started", { form: trackForm });
        window.location.href = data.audit_path;
      } else {
        setErrors(data.errors || { base: ["Something went wrong. Please try again."] });
        setSubmitting(false);
      }
    } catch {
      setErrors({ base: ["We couldn't reach the server. Please try again."] });
      setSubmitting(false);
    }
  };

  const fieldErrors = (key) => (errors[key] || []).join(", ");

  return (
    <form onSubmit={submit} className="text-left" noValidate>
      <label className="block mb-5">
        <span className="block text-sm font-semibold mb-1.5">Your Shopify store URL or myshopify domain</span>
        <input
          type="text"
          required
          placeholder="https://yourstore.com or your-store.myshopify.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink outline-none transition placeholder:text-ink/30 focus:border-sky-deep focus:ring-4 focus:ring-sky-wash"
          autoComplete="off"
          spellCheck="false"
        />
        {fieldErrors("domain") && <span className="mt-1 block text-sm text-red-600">{fieldErrors("domain")}</span>}
        {fieldErrors("myshopify_domain") && <span className="mt-1 block text-sm text-red-600">{fieldErrors("myshopify_domain")}</span>}
      </label>

      {fieldErrors("base") && <p className="mb-4 text-sm text-red-600">{fieldErrors("base")}</p>}

      <button type="submit" className="btn-primary w-full text-base" disabled={submitting}>
        {submitting ? "Starting…" : ctaLabel}
        {!submitting && <span aria-hidden="true">→</span>}
      </button>

      <p className="mt-4 text-center text-xs text-ink-soft">
        Free · No app install · No email needed to see your score
      </p>
    </form>
  );
}
